/**
 * Activity Service
 * Handles fetching and managing recent activity data
 */

import api from './api.service';

/**
 * Get recent activity feed
 * @param {Object} options Query options
 * @param {number} options.limit Number of activities to fetch (default: 20)
 * @param {string} options.startDate ISO date string for filtering activities from this date
 * @param {string} options.endDate ISO date string for filtering activities to this date
 */
export const getRecentActivity = async (options = {}) => {
  try {
    const { limit = 20, startDate, endDate } = options;

    console.log('Fetching recent activity with options:', options);

    // Build query parameters
    const params = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get('/activity/recent', { params });

    console.log('Recent activity response:', {
      status: response.status,
      dataLength: response.data?.data?.length || 0,
      total: response.data?.total || 0,
      dateRange: startDate && endDate ? `${startDate} to ${endDate}` : 'all'
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    throw error;
  }
};

/**
 * Get activity logs (planning actions, job actions, etc.)
 * @param {Object} options Query options
 * @param {number} options.limit Number of logs to fetch (default: 20)
 * @param {number} options.offset Number of logs to skip (for pagination)
 * @param {string} options.jobId Filter by job ID
 * @param {string[]} options.actionTypes Filter by action types
 */
export const getActivityLogs = async (options = {}) => {
  try {
    const { limit = 20, offset = 0, jobId, siteId, actionTypes, searchTerm } = options;

    console.log('Fetching activity logs with options:', options);

    const params = { limit, offset };
    if (jobId) params.jobId = jobId;
    if (siteId) params.siteId = siteId;
    if (actionTypes && actionTypes.length > 0) params.actionTypes = actionTypes.join(',');
    if (searchTerm && searchTerm.trim()) params.searchTerm = searchTerm.trim();
    
    const response = await api.get('/activity/logs', { params });
    
    console.log('🔍 Activity service raw response:', response);
    console.log('🔍 Response data structure:', {
      status: response.status,
      responseData: response.data,
      responseDataType: typeof response.data,
      responseDataKeys: response.data ? Object.keys(response.data) : 'null',
      dataField: response.data?.data,
      dataFieldLength: response.data?.data?.length || 0,
      total: response.data?.total || response.data?.meta?.total,
      success: response.data?.success
    });
    
    // Handle BAVS standard response format
    if (response.data?.success !== undefined) {
      // New standardized format: {success: true, data: [...], meta: {total: N}}
      return {
        success: response.data.success,
        data: response.data.data || [],
        total: response.data.meta?.total || response.data.data?.length || 0
      };
    } else if (Array.isArray(response.data)) {
      // Direct array response (current format)
      return {
        success: true,
        data: response.data,
        total: response.data.length
      };
    } else {
      // Fallback
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    throw error;
  }
};

/**
 * Format activity timestamp for display
 * @param {string} timestamp ISO timestamp
 * @returns {string} Human-readable time difference
 */
export const formatActivityTime = (timestamp) => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now - activityTime;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return activityTime.toLocaleDateString();
  }
};

/**
 * Group activities by time periods
 * @param {Array} activities Array of activity objects
 * @returns {Object} Activities grouped by time periods
 */
export const groupActivitiesByTime = (activities) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  };
  
  activities.forEach(activity => {
    const activityDate = new Date(activity.timestamp);
    
    if (activityDate >= today) {
      groups.today.push(activity);
    } else if (activityDate >= yesterday) {
      groups.yesterday.push(activity);
    } else if (activityDate >= weekAgo) {
      groups.thisWeek.push(activity);
    } else {
      groups.older.push(activity);
    }
  });
  
  return groups;
};

// Export as default object for consistency with other services
const activityService = {
  getRecentActivity,
  getActivityLogs,
  formatActivityTime,
  groupActivitiesByTime
};

export default activityService;